// Text-to-Speech service using ElevenLabs via Cloudflare Worker with caching

export class TTSService {
  private audioContext: AudioContext | null = null;
  private audioCache: Map<string, AudioBuffer> = new Map();
  private pendingRequests: Map<string, Promise<AudioBuffer>> = new Map();
  private activeNodes: Set<AudioBufferSourceNode> = new Set();
  private workerUrl: string;
  private enabled: boolean = true;
  private volume: number = 1.0;

  constructor() {
    // Determine worker URL based on environment
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    this.workerUrl = isDev ? 'http://localhost:8788' : 'https://wizard-duel-api.neenhouse.workers.dev';
  }

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    return this.audioContext;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Speak the given text using TTS
   * Returns immediately, audio plays asynchronously
   */
  async speak(text: string): Promise<void> {
    if (!this.enabled || !text) return;

    try {
      const audioBuffer = await this.getAudioBuffer(text);
      await this.playBuffer(audioBuffer);
    } catch (error) {
      console.warn('TTS playback failed:', error);
    }
  }

  /**
   * Pre-load audio for text to cache it
   * Useful for preloading common game phrases
   */
  async preload(text: string): Promise<void> {
    try {
      await this.getAudioBuffer(text);
    } catch (error) {
      console.warn('TTS preload failed:', error);
    }
  }

  /**
   * Pre-load multiple phrases in parallel
   */
  async preloadAll(phrases: string[]): Promise<void> {
    await Promise.allSettled(phrases.map(phrase => this.preload(phrase)));
  }

  private async getAudioBuffer(text: string): Promise<AudioBuffer> {
    // Check local cache first
    const cacheKey = text.toLowerCase().trim();
    const cached = this.audioCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Check if request is already pending
    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      return pending;
    }

    // Create new request
    const requestPromise = this.fetchAndDecode(text);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const audioBuffer = await requestPromise;
      this.audioCache.set(cacheKey, audioBuffer);
      return audioBuffer;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  private async fetchAndDecode(text: string): Promise<AudioBuffer> {
    const response = await fetch(`${this.workerUrl}/api/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      throw new Error(`TTS request failed: ${response.status}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const audioContext = this.getAudioContext();
    return audioContext.decodeAudioData(arrayBuffer);
  }

  private async playBuffer(audioBuffer: AudioBuffer): Promise<void> {
    const audioContext = this.getAudioContext();

    // Resume audio context if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    const source = audioContext.createBufferSource();
    const gainNode = audioContext.createGain();

    source.buffer = audioBuffer;
    gainNode.gain.value = this.volume;

    source.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Track active nodes for cleanup
    this.activeNodes.add(source);
    source.onended = () => {
      source.disconnect();
      gainNode.disconnect();
      this.activeNodes.delete(source);
    };

    source.start(0);
  }

  dispose(): void {
    // Stop and disconnect all active audio sources
    this.activeNodes.forEach(source => {
      try {
        source.stop();
        source.disconnect();
      } catch {
        // Ignore errors if already stopped
      }
    });
    this.activeNodes.clear();

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.audioCache.clear();
    this.pendingRequests.clear();
  }
}

// Game-specific TTS phrases
export const GAME_PHRASES = {
  // Welcome
  WELCOME: 'Welcome to Wizard Duel!',

  // Game flow
  WIZARD_DUEL: 'Wizard Duel!',
  FIGHT: 'Fight!',
  GAME_OVER: 'Game Over!',
  VICTORY: 'Victory!',
  DEFEAT: 'You have been defeated!',
  DRAW: 'It\'s a draw!',

  // Wizard selection
  CHOOSE_YOUR_WIZARD: 'Choose your wizard',
  PYROMANCER: 'Pyromancer, master of fire',
  FROSTMAGE: 'Frost Mage, wielder of ice',
  BATTLEMAGE: 'Battle Mage, warrior of magic',
  ARCHMAGE: 'Archmage, supreme sorcerer',

  // Spells
  FIREBALL: 'Fireball!',
  ICE_SHARD: 'Ice Shard!',
  LIGHTNING_BOLT: 'Lightning Bolt!',
  SHIELD: 'Shield!',
  HEAL: 'Heal!',

  // Combat
  CRITICAL_HIT: 'Critical hit!',
  BLOCKED: 'Blocked!',
  MISS: 'Miss!',

  // Multiplayer
  WAITING_FOR_OPPONENT: 'Waiting for opponent',
  OPPONENT_JOINED: 'Opponent has joined',
  SPECTATORS_WATCHING: 'Spectators are watching',
};

// Singleton instance
let ttsServiceInstance: TTSService | null = null;

export function getTTSService(): TTSService {
  if (!ttsServiceInstance) {
    ttsServiceInstance = new TTSService();
  }
  return ttsServiceInstance;
}
